/**
 * The small honest parts every board cell is made of.
 *
 *   StatusBar    the board's word FILLED with its Monday colour, as Monday draws
 *                it (FilledStatusChip: the ink is chosen per fill for contrast,
 *                bold where neither ink reaches 4.5:1). The word is always on
 *                the chip. A colour the server does not hold is drawn hatched
 *                and said in words.
 *   NotRecorded  "not recorded", never 0 and never a bare dash, with the
 *                reason available to a screen reader.
 *   SourceTag    who wrote the value: "Monday" (the Monday sync's copy, legacy
 *                reference since the authority ruling of 2026-10-05) or
 *                "eMerge". It says "Monday", never "board": the board is
 *                eMerge's, and Monday is the system the value came from.
 *   StateMark    a state word led by its Tide marker shape in its colour (an
 *                eMerge state in a cell). The legend draws the same component.
 *   GroupMark    a Monday group as a row's group column shows it: the group's
 *                title in ink, sourced to the Monday mirror. The legend draws
 *                the same component for a Monday-group tab.
 */
import React from 'react';
import { Marker } from '../tide';
import type { MarkerShape } from '../tide';
import type { CellSource } from './types';
import FilledStatusChip from '../status/FilledStatusChip';

export const NOT_RECORDED = 'not recorded';

export function NotRecorded({ why, label = NOT_RECORDED }: { why?: string; label?: string }) {
  return (
    <span className="board-na" title={why}>
      {label}
      {why ? <span className="board-sr"> — {why}</span> : null}
    </span>
  );
}

export function SourceTag({ source, detail }: { source: CellSource; detail?: string }) {
  const word = source === 'EMERGE' ? 'eMerge' : 'Monday';
  return (
    <span className={`board-src board-src--${source === 'EMERGE' ? 'emerge' : 'board'}`}>
      {word}{detail ? ` · ${detail}` : ''}
    </span>
  );
}

export function StatusBar({ word, color, note, emptyLabel = 'No status' }: { word: string | null | undefined; color: string | null | undefined; note?: string | null; emptyLabel?: string }) {
  if (!word) {
    return <span className="board-status board-status--empty"><span className="board-status__word">{emptyLabel}</span></span>;
  }
  const uncoloured = !color;
  if (!uncoloured) {
    return <FilledStatusChip word={word} hex={color} className="board-status-chip" />;
  }
  return (
    <span
      className={`board-status${uncoloured ? ' board-status--uncoloured' : ''}`}
      style={uncoloured ? undefined : ({ '--board-bar': color } as React.CSSProperties)}
      title={uncoloured ? (note || 'eMerge does not hold this word\'s Monday colour.') : undefined}
    >
      <span className="board-status__word">{word}</span>
      {uncoloured && <span className="board-sr"> (colour not recorded)</span>}
    </span>
  );
}

/** A dated value with its source on a second line; tentative says so in words. */
export function DatedCell({ on, source, what, tentative = false, none }: { on: string | null | undefined; source: CellSource; what?: string; tentative?: boolean; none?: React.ReactNode }) {
  if (!on) return <>{none ?? <NotRecorded />}</>;
  return (
    <span className="board-dated">
      <span className="board-dated__value">{on}{tentative ? <span className="board-tentative"> tentative</span> : null}</span>
      <SourceTag source={source} detail={what} />
    </span>
  );
}

export const NO_GROUP_LABEL = 'not in a group (yet)';

/** A Monday group in a cell (and its legend sample): the title in ink, then where it was read from. No group is said in words. */
export function GroupMark({ title }: { title: string | null | undefined }) {
  if (!title) return <NotRecorded label={NO_GROUP_LABEL} />;
  return (
    <span className="board-groupmark">
      <span className="board-groupmark__title">{title}</span>
      <SourceTag source="MONDAY" detail="mirror" />
    </span>
  );
}

/**
 * A state word led by its non-colour marker; the word stays in ink. `sub` is
 * an optional plain-ink qualifier under the word (a closed building's closure
 * kind); the legend's sample never passes one, so the sample stays the word.
 */
export function StateMark({ word, color, marker, stateKey, sub }: { word: string; color: string | null | undefined; marker: MarkerShape; stateKey?: string; sub?: React.ReactNode }) {
  return (
    <span className="board-mark" data-state={stateKey}>
      <Marker shape={marker} color={color || 'var(--tide-status-missing)'} size={12} />
      <strong>{word}</strong>
      {sub ? <span className="board-src board-mark__sub">{sub}</span> : null}
    </span>
  );
}
